import React, {useState, useMemo, useEffect} from 'react';
import {useTable, useRowSelect} from 'react-table';
import {ContentWrapper} from '@components';
import {useHistory} from 'react-router-dom';
import {X} from 'react-feather';
import {ReactComponent as SearchIcon} from '@images/search.svg';
import {toggleUserActiveStatus} from '@apis';
import {Switch} from '@components';
import './users.css';
import {API_HOST} from '@apis/';
import axios from 'axios';
import {languageToAcceptLanguage, toastMessageKey} from '@constants/languages';
import {useTranslation} from 'react-i18next';
import i18n from '../../i18n';
import {getAbsoluteImageUrl} from '../../utils/imageUtils';
const IndeterminateCheckbox = React.forwardRef(
  ({indeterminate, ...rest}, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    React.useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return (
      <>
        <input type="checkbox" ref={resolvedRef} {...rest} />
      </>
    );
  },
);

const Table = ({columns, data}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    state: {selectedRowIds},
  } = useTable({columns, data}, useRowSelect, hooks => {
    hooks.visibleColumns.push(columns => [
      {
        id: 'selection',
        Header: ({getToggleAllRowsSelectedProps}) => (
          <div>
            <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />
          </div>
        ),
        Cell: ({row}) => (
          <div>
            <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />
          </div>
        ),
      },
      ...columns,
    ]);
  });

  return (
    <table {...getTableProps()} className="min-w-full">
      <thead className="bg-tableHeaderBg p-4">
        {headerGroups.map(headerGroup => (
          <tr key={headerGroup.id} {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map(column => (
              <th
                key={column.id}
                {...column.getHeaderProps()}
                className="text-left text-menu font-normal uppercase text-sm px-4 py-2">
                {column.render('Header')}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody {...getTableBodyProps()}>
        {rows.map(row => {
          prepareRow(row);
          return (
            <tr key={row.id} {...row.getRowProps()}>
              {row.cells.map(cell => {
                if (cell.column.id === 'is_active') {
                  return (
                    <td
                      key={cell.column.id}
                      {...cell.getCellProps()}
                      className="p-4 whitespace-nowrap text-sm">
                      <Switch
                        enabled={row.values.is_active}
                        onChange={() =>
                          toggleUserActiveStatus(row.original.user.id)
                        }
                      />
                    </td>
                  );
                }

                return (
                  <td
                    key={cell.column.id}
                    {...cell.getCellProps()}
                    className="p-4 whitespace-nowrap text-sm">
                    {cell.render('Cell')}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const ShopUsers = () => {
  const [data, setData] = useState([]);
  const [sloading, setSloading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [nextPage, setNextPage] = useState('');
  const [previousPage, setPreviousPage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const history = useHistory();
  const rowclick = id => {
    history.replace('/shop-users/' + id);
  };

  const {t} = useTranslation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPageNumber(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getUsers = () => {
    const token =
      localStorage.getItem('nls:auth') || sessionStorage.getItem('nls:auth');
    setSloading(true);
    const searchParam = debouncedSearch
      ? `&q=${encodeURIComponent(debouncedSearch)}`
      : '';
    let config = {
      url: API_HOST + `/admin-api/v1/shop-users/?page=${pageNumber}${searchParam}`,
      method: 'get',
      headers: {
        'Accept-Language': languageToAcceptLanguage(i18n.language),
        Authorization: `Token ${token}`,
      },
    };
    axios(config).then(res => {
      let shortData = res.data.results?.sort((a, b) =>
        a.user.first_name.localeCompare(b.user.first_name),
      );

      setData(shortData);

      setNextPage(res.data.next);
      setPreviousPage(res.data.previous);
      setSloading(false);
    });
  };

  useEffect(() => {
    getUsers();
  }, [pageNumber, debouncedSearch]);

  const levelTranslations = {
    en: {
      Gold: 'Gold',
      Silver: 'Silver',
      Bronze: 'Bronze',
      New: 'New',
    },
    es: {
      Gold: 'Oro',
      Silver: 'Plata',
      Bronze: 'Bronce',
      New: 'Nuevo',
    },
  };

  const translateLevel = value => {
    const localeKey = toastMessageKey(i18n.language);
    return levelTranslations[localeKey][value] || value;
  };

  const columns = useMemo(
    () => [
      {
        Header: t('shopUsers.user'),
        accessor: 'user',
        Cell: ({row}) => (
          <div
            className="flex items-center cursor-pointer"
            onClick={() => rowclick(row.original.id)}>
            <div>
              {row.values.user?.profile &&
              row.values.user.profile.profile_image ? (
                <img
                  src={getAbsoluteImageUrl(
                    row.values.user.profile.profile_image,
                  )}
                  className="user-avatar"
                  alt="User avatar"
                />
              ) : (
                <img
                  src="https://cdn-icons-png.flaticon.com/512/1077/1077063.png"
                  className="user-avatar"
                  alt="User avatar"
                />
              )}
            </div>
            <div className="px-4">
              <p className="text-left">
                {row.values.user?.first_name || ''}{' '}
                {row.values.user?.last_name || ''}
              </p>
            </div>
          </div>
        ),
      },
      {Header: t('shopUsers.pending'), accessor: 'pending_approval_count'},
      {Header: t('shopUsers.purchases'), accessor: 'purchases_count'},
      {
        Header: t('shopUsers.level'),
        accessor: 'level_display',
        Cell: ({value}) => translateLevel(value),
      },
      {Header: t('shopUsers.points'), accessor: 'total_points'},
      {
        Header: t('shopUsers.spent'),
        accessor: 'total_money_spent',
        Cell: ({value}) => (
          <div>
            <p className="text-left">{value === null ? '0' : value}</p>
          </div>
        ),
      },
      {Header: t('shopUsers.activeStatus'), accessor: 'is_active'},
    ],
    [t],
  );

  return (
    <div>
      <ContentWrapper
        hasSearch={false}
        breadcrumb={[
          {title: t('sidebar.menuItems.shopUsers'), link: '/shop-users'},
        ]}>
        <div className="flex flex-col w-full">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              gap: '16px',
              flexWrap: 'wrap',
            }}>
            <h5 className="mb-0">{t('shopUsers.users')}</h5>
            <div
              className="relative"
              style={{minWidth: '240px', maxWidth: '320px', flex: '1'}}>
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('approve.searchCustomer')}
                className={`border border-gray-200 focus:outline-none focus:border-indigo-700 rounded w-full text-sm text-gray-700 bg-gray-100 pl-10 py-2 ${
                  searchQuery ? 'pr-9' : 'pr-3'
                }`}
              />
              <div className="text-gray-500 absolute left-3 top-0 bottom-0 flex items-center pointer-events-none">
                <SearchIcon height={16} />
              </div>
              {searchQuery ? (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-0 bottom-0 flex items-center text-gray-500 hover:text-gray-700"
                  aria-label={t('approve.clearSearch')}>
                  <X size={16} />
                </button>
              ) : null}
            </div>
          </div>
          {!sloading && (
            <>
            <Table columns={columns} data={data} />
            {data && data.length === 0 ? (
              <div>
                <p className="text-center mt-5">{t('shopUsers.noData')}</p>
              </div>
            ) : (
              <div>
                <div className="bg-tableHeaderBg py-2 md:flex md:justify-center gap-x-10 px-4">
                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      previousPage !== null ? 'cursor-pointer' : 'pointer-none'
                    }`}
                    onClick={() => {
                      setPageNumber(pageNumber - 1);
                    }}>
                    {t('shopUsers.showPrevious')}
                  </button>

                  <button
                    style={{
                      backgroundColor: '#970c24d9',
                      padding: 10,
                      color: '#fff',
                      borderRadius: 5,
                    }}
                    className={`text-xs text-center text-wine ${
                      nextPage !== null ? 'cursor-pointer' : 'pointer-none'
                    }`}
                    onClick={() => setPageNumber(pageNumber + 1)}>
                    {t('shopUsers.showNext')}
                  </button>
                </div>
              </div>
            )}
            </>
          )}
        </div>
      </ContentWrapper>
    </div>
  );
};

export default ShopUsers;
